import { LogOut, Pencil, ShieldCheck, UserMinus, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Avatar } from '@/components/ui/Avatar'
import { Button, IconButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dialog } from '@/components/ui/Dialog'
import { Drawer } from '@/components/ui/Drawer'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/features/auth/useAuth'
import { ToggleRow } from '@/features/lms/components/ToggleRow'
import { useAddMembers, useRemoveMember, useUpdateConversation } from '../api'
import { roleLabel } from '../format'
import type { Contact, ConversationDetail } from '../types'
import { ContactPicker } from './ContactPicker'
import { ConversationAvatar } from './ConversationList'

interface ConversationInfoProps {
  conversation: ConversationDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  backHref: string
}

/** Members and settings: posting in a class room, name and members of a group, leaving it. */
export function ConversationInfo({ conversation: c, open, onOpenChange, backHref }: ConversationInfoProps) {
  const { t } = useTranslation('messaging')
  const { user } = useAuth()
  const navigate = useNavigate()
  const update = useUpdateConversation(c.id)
  const add = useAddMembers(c.id)
  const remove = useRemoveMember(c.id)
  const [renaming, setRenaming] = useState(false)
  const [title, setTitle] = useState(c.title)
  const [adding, setAdding] = useState(false)
  const [picked, setPicked] = useState<Contact[]>([])
  const [leaving, setLeaving] = useState(false)

  const fail = (what: string) => (e: unknown) => toast.error(what, toApiError(e).message)

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('Details')}
      side="right"
      footer={
        c.canLeave ? (
          <Button variant="danger-soft" leftIcon={<LogOut />} onClick={() => setLeaving(true)}>
            {t('Leave the group')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col items-center gap-2 pb-5 text-center">
        <ConversationAvatar kind={c.kind} title={c.title} src={c.other?.avatarUrl} />
        {renaming ? (
          <form
            className="flex w-full gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              update.mutate(
                { title },
                { onSuccess: () => setRenaming(false), onError: fail(t('Could not rename the group')) },
              )
            }}
          >
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              autoFocus
              aria-label={t('Group name')}
            />
            <Button type="submit" loading={update.isPending} disabled={title.trim() === ''}>
              {t('Save')}
            </Button>
          </form>
        ) : (
          <p className="flex items-center gap-1 text-base font-semibold text-ink">
            {c.title}
            {c.kind === 'group' && c.moderator ? (
              <IconButton
                label={t('Rename')}
                icon={<Pencil />}
                size="icon-sm"
                variant="ghost"
                onClick={() => {
                  setTitle(c.title)
                  setRenaming(true)
                }}
              />
            ) : null}
          </p>
        )}
        <p className="text-xs text-muted">
          {c.kind === 'class'
            ? t('Class room: the students of the class and the teachers who teach it.')
            : c.kind === 'group'
              ? t('Group')
              : c.other
                ? roleLabel(c.other.role)
                : ''}
        </p>
      </div>

      {c.kind === 'class' && c.moderator ? (
        <div className="mb-5">
          <ToggleRow
            label={t('Students can post')}
            hint={t('When off, only teachers write; students still read and join live classes.')}
            checked={c.studentsCanPost}
            onCheckedChange={(on) =>
              update.mutate({ studentsCanPost: on }, { onError: fail(t('Could not save')) })
            }
          />
        </div>
      ) : null}

      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink">{t('{{count}} members', { count: c.memberCount })}</h3>
        {c.canManageMembers ? (
          <Button size="sm" variant="secondary" leftIcon={<UserPlus />} onClick={() => setAdding(true)}>
            {t('Add people')}
          </Button>
        ) : null}
      </div>
      <ul className="divide-y divide-line">
        {c.members.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-2">
            <Avatar name={m.name} src={m.avatarUrl} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink">
                {m.name}
                {m.id === user?.id ? <span className="font-normal text-muted"> ({t('you')})</span> : null}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted">
                {roleLabel(m.role)}
                {m.moderator ? (
                  <span className="inline-flex items-center gap-0.5 text-brand-700">
                    · <ShieldCheck className="size-3" aria-hidden="true" />
                    {c.kind === 'class' ? t('Teacher of the class') : t('Organiser')}
                  </span>
                ) : null}
              </span>
            </span>
            {c.canManageMembers && m.id !== user?.id ? (
              <IconButton
                label={t('Remove {{name}}', { name: m.name })}
                icon={<UserMinus />}
                size="icon-sm"
                variant="ghost"
                onClick={() => remove.mutate(m.id, { onError: fail(t('Could not remove this person')) })}
              />
            ) : null}
          </li>
        ))}
      </ul>

      <Dialog
        open={adding}
        onOpenChange={(o) => {
          setAdding(o)
          if (!o) setPicked([])
        }}
        title={t('Add people')}
        footer={
          <Button
            loading={add.isPending}
            disabled={picked.length === 0}
            onClick={() =>
              add.mutate(
                picked.map((p) => p.id),
                {
                  onSuccess: () => {
                    setAdding(false)
                    setPicked([])
                  },
                  onError: fail(t('Could not add these people')),
                },
              )
            }
          >
            {t('Add {{count}} people', { count: picked.length })}
          </Button>
        }
      >
        <ContactPicker
          multiple
          exclude={c.members.map((m) => m.id)}
          selected={picked}
          onToggle={(p) =>
            setPicked((all) =>
              all.some((x) => x.id === p.id) ? all.filter((x) => x.id !== p.id) : [...all, p],
            )
          }
        />
      </Dialog>

      <ConfirmDialog
        open={leaving}
        onOpenChange={setLeaving}
        title={t('Leave “{{title}}”?', { title: c.title })}
        description={t('You will no longer receive its messages. An organiser can add you again.')}
        confirmLabel={t('Leave the group')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() =>
          user &&
          remove.mutate(user.id, {
            onSuccess: () => {
              setLeaving(false)
              onOpenChange(false)
              void navigate(backHref)
            },
            onError: fail(t('Could not leave the group')),
          })
        }
      />
    </Drawer>
  )
}
